import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Mail, CheckCircle, XCircle, Search, RefreshCw, Calendar, Loader, Eye, Paperclip, Download, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import Header from '@/components/Header';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EmailReports = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [logs, setLogs] = useState([]);
  const [filters, setFilters] = useState({
    magazine: '',
    email_type: '',
    status: '',
    date_from: '',
    date_to: ''
  });
  const [stats, setStats] = useState({
    total: 0,
    sent: 0,
    failed: 0,
    renewal: 0,
    magazine: 0
  });
  
  // Preview modal state
  const [showPreview, setShowPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [selectedLog, setSelectedLog] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    fetchLogs();
  }, []);

  // Auto-search when any filter changes
  useEffect(() => {
    const hasFilters = Object.values(filters).some(v => v);
    if (hasFilters) {
      fetchLogs(filters);
    }
  }, [filters.magazine, filters.email_type, filters.status]);

  const handleSyncStatus = async () => {
    setSyncing(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/email/sync-status`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Status synced! ${response.data.updated} emails updated`);
      fetchLogs();
    } catch (error) {
      console.error('Error syncing status:', error);
      toast.error('Failed to sync email status');
    } finally {
      setSyncing(false);
    }
  };

  // Preview email content (for ad_proposal types that have associated proposals)
  const handlePreviewEmail = async (log) => {
    setSelectedLog(log);
    setShowPreview(true);
    setPreviewLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      
      // If this is an ad_proposal email, fetch the proposal preview
      if (log.email_type === 'ad_proposal' || log.email_type === 'ad_proposal_test') {
        // Try to fetch the proposal by ID if we have it
        const proposalId = log.proposal_id;
        if (proposalId) {
          const response = await axios.get(`${API}/ad-proposals?limit=100`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const proposal = response.data.find(p => p.id === proposalId);
          
          if (proposal) {
            // Generate preview
            const previewResponse = await axios.post(`${API}/ad-proposals/preview`, {
              magazine: proposal.magazine,
              contact_name: proposal.contact_name,
              company_name: proposal.company_name,
              proposal_type: proposal.proposal_type,
              proposal_date: proposal.proposal_date,
              ad_positions: proposal.ad_positions || [],
              discount_percent: proposal.discount_percent || 0,
              terms_conditions: proposal.terms_conditions || '',
              special_remarks: proposal.special_remarks || '',
              banner_image_url: proposal.banner_image_url || ''
            }, {
              headers: { Authorization: `Bearer ${token}` }
            });
            setPreviewHtml(previewResponse.data.html);
          } else {
            setPreviewHtml(`<div style="padding: 40px; text-align: center; color: #666;">
              <h3>Preview Not Available</h3>
              <p>The original proposal data could not be found.</p>
              <p><strong>Subject:</strong> ${log.subject}</p>
              <p><strong>Sent to:</strong> ${log.recipient_email}</p>
            </div>`);
          }
        } else {
          setPreviewHtml(`<div style="padding: 40px; text-align: center; color: #666;">
            <h3>Preview Not Available</h3>
            <p>No proposal ID linked to this email log.</p>
          </div>`);
        }
      } else {
        // For other email types, show basic info
        setPreviewHtml(`<div style="padding: 40px; font-family: Arial, sans-serif;">
          <h2 style="color: #7c3aed;">${log.subject}</h2>
          <hr style="border: 1px solid #e5e7eb; margin: 20px 0;" />
          <p><strong>Email Type:</strong> ${getEmailTypeLabel(log.email_type)}</p>
          <p><strong>Recipient:</strong> ${log.recipient_name} (${log.recipient_email})</p>
          <p><strong>Magazine:</strong> ${log.magazine}</p>
          <p><strong>Sent At:</strong> ${formatDate(log.sent_at)}</p>
          <p><strong>Sent By:</strong> ${log.sent_by}</p>
          ${log.sender_email ? `<p><strong>From Email:</strong> ${log.sender_email}</p>` : ''}
          <hr style="border: 1px solid #e5e7eb; margin: 20px 0;" />
          <p style="color: #666; font-style: italic;">Full email preview is only available for Advertisement Proposals.</p>
        </div>`);
      }
    } catch (error) {
      console.error('Error loading preview:', error);
      setPreviewHtml(`<div style="padding: 40px; text-align: center; color: #f00;">
        <h3>Error Loading Preview</h3>
        <p>${error.message}</p>
      </div>`);
    } finally {
      setPreviewLoading(false);
    }
  };

  const fetchLogs = async (searchFilters = {}) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Use search endpoint if filters provided, otherwise get all
      const hasFilters = Object.values(searchFilters).some(v => v);
      const response = hasFilters 
        ? await axios.post(`${API}/email/logs/search`, searchFilters, {
            headers: { Authorization: `Bearer ${token}` },
            timeout: 30000
          })
        : await axios.get(`${API}/email/logs`, {
            headers: { Authorization: `Bearer ${token}` },
            timeout: 30000
          });
      
      const logsData = Array.isArray(response.data) ? response.data : [];
      setLogs(logsData);
      
      // Calculate stats
      setStats({
        total: logsData.length,
        sent: logsData.filter(l => l.status === 'sent' || l.status === 'delivered' || l.status === 'opened').length,
        failed: logsData.filter(l => l.status === 'failed' || l.status === 'bounced').length,
        renewal: logsData.filter(l => l.email_type === 'renewal_reminder' || l.email_type === 'renewal_test').length,
        magazine: logsData.filter(l => l.email_type === 'monthly_magazine' || l.email_type === 'magazine_test').length
      });
      
    } catch (error) {
      console.error('Error fetching logs:', error);
      const errMsg = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Failed to fetch email logs: ${errMsg}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    fetchLogs(filters);
  };

  const handleReset = () => {
    setFilters({
      magazine: '',
      email_type: '',
      status: '',
      date_from: '',
      date_to: ''
    });
    fetchLogs();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getEmailTypeLabel = (type) => {
    const labels = {
      'renewal_reminder': 'Renewal Reminder',
      'renewal_test': 'Renewal Test',
      'ad_proposal': 'Ad Proposal',
      'monthly_magazine': 'Monthly Magazine',
      'magazine_test': 'Magazine Test',
      'test': 'Test Email'
    };
    return labels[type] || type;
  };

  const getEmailTypeColor = (type) => {
    const colors = {
      'renewal_reminder': '#7c3aed',
      'renewal_test': '#a78bfa',
      'ad_proposal': '#059669',
      'monthly_magazine': '#2563eb',
      'magazine_test': '#60a5fa',
      'test': '#6b7280'
    };
    return colors[type] || '#6b7280';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-purple-50 py-8">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            <Button variant="outline" onClick={() => navigate('/')}>
              <ArrowLeft className="mr-2" size={18} />
              Back
            </Button>
            <h1 className="text-4xl font-bold" style={{ color: '#7c3aed' }}>
              Email Reports
            </h1>
          </div>
          <div className="flex space-x-3">
            <Button 
              onClick={handleSyncStatus} 
              variant="outline"
              disabled={syncing}
              className="border-green-300 text-green-700"
            >
              {syncing ? <Loader className="mr-2 animate-spin" size={18} /> : <RefreshCw className="mr-2" size={18} />}
              Sync Status from Brevo
            </Button>
            <Button 
              onClick={() => fetchLogs()} 
              variant="outline"
              disabled={loading}
            >
              <RefreshCw className={`mr-2 ${loading ? 'animate-spin' : ''}`} size={18} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Info Banner */}
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-6">
          <p className="text-sm text-blue-700">
            <strong>Email Tracking:</strong> Click "Sync Status from Brevo" to update email delivery and open status. 
            Status can be: <span className="text-green-600 font-medium">Sent</span> → <span className="text-blue-600 font-medium">Delivered</span> → <span className="text-purple-600 font-medium">Opened</span> or <span className="text-orange-600 font-medium">Bounced</span>
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-5 gap-4 mb-8">
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <div className="flex items-center space-x-3">
              <Mail className="text-blue-600" size={24} />
              <div>
                <p className="text-2xl font-bold text-blue-800">{stats.total}</p>
                <p className="text-sm text-blue-600">Total Emails</p>
              </div>
            </div>
          </div>
          
          <div className="bg-green-50 p-4 rounded-lg border border-green-200">
            <div className="flex items-center space-x-3">
              <CheckCircle className="text-green-600" size={24} />
              <div>
                <p className="text-2xl font-bold text-green-800">{stats.sent}</p>
                <p className="text-sm text-green-600">Sent/Delivered</p>
              </div>
            </div>
          </div>
          
          <div className="bg-red-50 p-4 rounded-lg border border-red-200">
            <div className="flex items-center space-x-3">
              <XCircle className="text-red-600" size={24} />
              <div>
                <p className="text-2xl font-bold text-red-800">{stats.failed}</p>
                <p className="text-sm text-red-600">Failed/Bounced</p>
              </div>
            </div>
          </div>
          
          <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
            <div className="flex items-center space-x-3">
              <Calendar className="text-purple-600" size={24} />
              <div>
                <p className="text-2xl font-bold text-purple-800">{stats.renewal}</p>
                <p className="text-sm text-purple-600">Renewals</p>
              </div>
            </div>
          </div>
          
          <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200">
            <div className="flex items-center space-x-3">
              <Mail className="text-indigo-600" size={24} />
              <div>
                <p className="text-2xl font-bold text-indigo-800">{stats.magazine}</p>
                <p className="text-sm text-indigo-600">Magazine Emails</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="form-section mb-8">
          <h2 className="text-xl font-bold mb-4" style={{ color: '#7c3aed' }}>
            <Search className="inline mr-2" size={20} />
            Filter Emails
          </h2>
          
          <div className="grid grid-cols-6 gap-4">
            <div className="space-y-2">
              <Label>Magazine</Label>
              <select
                value={filters.magazine}
                onChange={(e) => setFilters({ ...filters, magazine: e.target.value })}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="">All</option>
                <option value="Aabkari Times">Aabkari Times</option>
                <option value="Sugar Times">Sugar Times</option>
              </select>
            </div>
            
            <div className="space-y-2">
              <Label>Email Type</Label>
              <select
                value={filters.email_type}
                onChange={(e) => setFilters({ ...filters, email_type: e.target.value })}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="">All</option>
                <option value="renewal_reminder">Renewal Reminder</option>
                <option value="renewal_test">Renewal Test</option>
                <option value="monthly_magazine">Monthly Magazine</option>
                <option value="magazine_test">Magazine Test</option>
                <option value="ad_proposal">Ad Proposal</option>
                <option value="test">Test Email</option>
              </select>
            </div>
            
            <div className="space-y-2">
              <Label>Status</Label>
              <select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="">All</option>
                <option value="sent">Sent</option>
                <option value="delivered">Delivered</option>
                <option value="opened">Opened</option>
                <option value="bounced">Bounced</option>
                <option value="failed">Failed</option>
              </select>
            </div>
            
            <div className="space-y-2">
              <Label>Date From</Label>
              <Input 
                type="date" 
                value={filters.date_from}
                onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Date To</Label>
              <Input 
                type="date" 
                value={filters.date_to}
                onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
              />
            </div>
            
            <div className="space-y-2 flex items-end space-x-2">
              <Button 
                onClick={handleSearch}
                className="flex-1"
                style={{ backgroundColor: '#7c3aed' }}
              >
                <Search className="mr-1" size={16} />
                Search
              </Button>
              <Button 
                onClick={handleReset}
                variant="outline"
              >
                Reset
              </Button>
            </div>
          </div>
        </div>

        {/* Email Logs Table */}
        <div className="form-section">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-100">
                  <th className="p-3 text-left">Date & Time</th>
                  <th className="p-3 text-left">Magazine</th>
                  <th className="p-3 text-left">Type</th>
                  <th className="p-3 text-left">Recipient</th>
                  <th className="p-3 text-left">Subject</th>
                  <th className="p-3 text-left">Sender</th>
                  <th className="p-3 text-left">Attachments</th>
                  <th className="p-3 text-left">Status</th>
                  <th className="p-3 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {logs.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="p-8 text-center text-gray-500">
                      <Mail className="mx-auto text-gray-400 mb-2" size={48} />
                      <p>No email logs found</p>
                    </td>
                  </tr>
                ) : (
                  logs.map((log, index) => (
                    <tr key={log.id || index} className="border-b hover:bg-gray-50">
                      <td className="p-3 text-gray-600 whitespace-nowrap">{formatDate(log.sent_at)}</td>
                      <td className="p-3">
                        <span className={`font-medium ${(log.magazine === 'Aabkari Times' || log.magazine === 'Aabkari Times ENG') ? 'text-blue-600' : 'text-green-600'}`}>
                          {log.magazine}
                        </span>
                      </td>
                      <td className="p-3">
                        <span 
                          className="px-2 py-1 rounded-full text-xs font-medium text-white"
                          style={{ backgroundColor: getEmailTypeColor(log.email_type) }}
                        >
                          {getEmailTypeLabel(log.email_type)}
                        </span>
                      </td>
                      <td className="p-3">
                        <div>
                          {(log.member_id || log.recipient_id) && (
                            <p className={`text-xs font-bold ${(log.member_id || log.recipient_id || '').startsWith('AT') ? 'text-blue-600' : 'text-green-600'}`}>
                              {log.member_id || log.recipient_id}
                            </p>
                          )}
                          <p className="font-medium">{log.recipient_name || '-'}</p>
                          <p className="text-gray-500 text-xs">{log.recipient_email}</p>
                          {log.recipient_company && (
                            <p className="text-gray-400 text-xs">{log.recipient_company}</p>
                          )}
                        </div>
                      </td>
                      <td className="p-3 max-w-xs truncate" title={log.subject}>
                        {log.subject}
                      </td>
                      <td className="p-3">
                        <div>
                          <p className="text-xs font-medium text-gray-700">{log.sent_by}</p>
                          {log.sender_email && (
                            <p className="text-xs text-gray-500">{log.sender_email}</p>
                          )}
                        </div>
                      </td>
                      <td className="p-3">
                        {log.attachments && log.attachments.length > 0 ? (
                          <div className="flex flex-col gap-1">
                            {log.attachments.map((att, idx) => (
                              <a 
                                key={idx}
                                href={att.startsWith('http') ? att : '#'}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center text-xs text-blue-600 hover:underline"
                                title={att}
                              >
                                <Paperclip size={12} className="mr-1" />
                                {att.split('/').pop()?.substring(0, 20) || 'Attachment'}...
                              </a>
                            ))}
                          </div>
                        ) : (
                          <span className="text-gray-400 text-xs">-</span>
                        )}
                      </td>
                      <td className="p-3">
                        {log.status === 'sent' && (
                          <span className="flex items-center text-green-600">
                            <CheckCircle size={16} className="mr-1" />
                            Sent
                          </span>
                        )}
                        {log.status === 'delivered' && (
                          <span className="flex items-center text-blue-600">
                            <CheckCircle size={16} className="mr-1" />
                            Delivered
                          </span>
                        )}
                        {log.status === 'opened' && (
                          <span className="flex items-center text-purple-600">
                            <CheckCircle size={16} className="mr-1" />
                            Opened
                          </span>
                        )}
                        {log.status === 'bounced' && (
                          <span className="flex items-center text-orange-600" title={log.error_message}>
                            <XCircle size={16} className="mr-1" />
                            Bounced
                          </span>
                        )}
                        {log.status === 'failed' && (
                          <span className="flex items-center text-red-600" title={log.error_message}>
                            <XCircle size={16} className="mr-1" />
                            Failed
                          </span>
                        )}
                      </td>
                      <td className="p-3">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handlePreviewEmail(log)}
                          title="Preview Email"
                        >
                          <Eye size={14} />
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Email Preview Modal */}
        {showPreview && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
              <div className="bg-purple-600 text-white p-4 flex items-center justify-between">
                <h2 className="text-xl font-bold flex items-center">
                  <Mail className="mr-2" size={24} />
                  Email Preview
                </h2>
                <button onClick={() => { setShowPreview(false); setSelectedLog(null); setPreviewHtml(''); }} className="text-white hover:text-gray-200">
                  ×
                </button>
              </div>
              
              {selectedLog && (
                <div className="p-4 bg-gray-50 border-b">
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">To:</span>
                      <span className="ml-2 font-medium">{selectedLog.recipient_name} ({selectedLog.recipient_email})</span>
                    </div>
                    <div>
                      <span className="text-gray-500">From:</span>
                      <span className="ml-2 font-medium">{selectedLog.sender_email || 'N/A'}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Sent:</span>
                      <span className="ml-2 font-medium">{formatDate(selectedLog.sent_at)}</span>
                    </div>
                  </div>
                  <div className="mt-2">
                    <span className="text-gray-500">Subject:</span>
                    <span className="ml-2 font-bold">{selectedLog.subject}</span>
                  </div>
                  {selectedLog.attachments && selectedLog.attachments.length > 0 && (
                    <div className="mt-2 flex items-center flex-wrap gap-2">
                      <span className="text-gray-500">Attachments:</span>
                      {selectedLog.attachments.map((att, idx) => (
                        <a 
                          key={idx}
                          href={att.startsWith('http') ? att : '#'}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs hover:bg-blue-200"
                        >
                          <Download size={12} className="mr-1" />
                          {att.split('/').pop()?.substring(0, 25) || 'Download'}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              )}
              
              <div className="overflow-y-auto" style={{ maxHeight: 'calc(90vh - 200px)' }}>
                {previewLoading ? (
                  <div className="p-12 text-center">
                    <Loader className="mx-auto animate-spin text-purple-600" size={48} />
                    <p className="mt-4 text-gray-500">Loading preview...</p>
                  </div>
                ) : (
                  <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailReports;
